import React, { useState, useMemo } from "react";
import yogaKnowledge from "../content/module_05a_yogas_knowledge.json";

/* =========================================================
   HELPERS
========================================================= */

function normalizeYogaCode(code = "") {
  if (!code) return null;

  const raw = code.toUpperCase().trim();

  const directKnowledgeMatch = yogaKnowledge?.yogas?.find(
    (k) => k.yoga_code === raw
  );
  if (directKnowledgeMatch) return raw;

  const directMap = {
    GAJA_KESARI: "GAJA_KESARI_YOGA",
    BUDH_ADITYA: "BUDHA_ADITYA_YOGA",
    CHANDRA_MANGALA: "CHANDRA_MANGALA_YOGA",
    DHARMA_KARMADHIPATI: "DHARMA_KARMADHIPATI_YOGA",
    DHAN_YOGA: "DHAN_YOGA_GENERIC",
    PARIVARTAN: "RAJ_YOGA_GENERIC",
    RAJ_YOGA: "RAJ_YOGA_GENERIC",
    VIPREET_RAJA: "VIPREET_RAJ_YOGA_GENERIC",
  };

  if (directMap[raw]) return directMap[raw];
  if (raw.endsWith("_YOGA")) return raw;

  return raw + "_YOGA";
}

function findKnowledge(yogaCode) {
  if (!yogaCode) return null;
  return yogaKnowledge.yogas.find((k) => k.yoga_code === yogaCode);
}

function BulletList({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul style={{ marginTop: 6, paddingLeft: 18 }}>
      {items.map((t, i) => (
        <li key={i} style={{ fontSize: 13, lineHeight: 1.65, color: "#cbd5e1" }}>
          {t}
        </li>
      ))}
    </ul>
  );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

function YogaAnalysisTab({ yogaAnalysis, mode = "REAL" }) {
  const safeAnalysis = useMemo(() => yogaAnalysis || {}, [yogaAnalysis]);
  const summary = safeAnalysis.summary;
  const enabled = safeAnalysis.enabled !== false;

  /* ---------------------------
     ALWAYS RUN HOOKS
  ---------------------------- */

  const enrichedYogas = useMemo(() => {
    const yogas = safeAnalysis?.yogas || [];

    return yogas.map((yoga) => {
      const resolvedCode = normalizeYogaCode(yoga.code);
      const knowledge = findKnowledge(resolvedCode);

      return {
        ...yoga,
        resolved_code: resolvedCode,
        knowledge,
        title:
          knowledge?.yoga_name ||
          yoga.code?.replaceAll("_", " ") ||
          "",
        core_meaning: knowledge?.core_meaning || [],
        activation_principles: knowledge?.activation_principles || [],
        positive_expression: knowledge?.positive_expression || [],
        challenging_expression: knowledge?.challenging_expression || [],
        remedial_guidance: knowledge?.remedial_guidance || [],
      };
    });
  }, [safeAnalysis]);

  const isMultiple = enrichedYogas.length > 1;
  const [openIndex, setOpenIndex] = useState(null);

  /* ---------------------------
     EARLY RETURNS
  ---------------------------- */

  if (!yogaAnalysis) return null;

  if (!enabled) {
    return (
      <div style={{ padding: 16 }}>
        🔒 Yoga analysis is not enabled for this chart.
      </div>
    );
  }

  /* =========================================================
     LOCK BLOCK (SUMMARY MODE)
  ========================================================= */

  const YogaLockBlock = () => (
    <div
      style={{
        marginTop: 36,
        padding: 20,
        borderRadius: 14,
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(148,163,184,0.15)",
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          color: "#64748b",
          marginBottom: 10,
        }}
      >
        Interpretive Depth
      </div>

      <div style={{ fontWeight: 600, marginBottom: 8 }}>
         🔒 Detailed Yoga Interpretation
      </div>

      <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.7 }}>
        This layer explores how yogas activate psychologically,
        how they unfold during planetary periods,
        and how their strength modifies expression.
        <br /><br />
        Insight plan provides full interpretive depth across all modules.
      </div>
    </div>
  );

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div style={{ fontSize: 13 }}>

      {/* =========================
         FIXED SUMMARY
      ========================== */}
      {summary && (
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            Yoga Summary
          </div>

          <div>Total detected: {summary.total_detected}</div>
          <div>Major yogas: {summary.major_yogas}</div>
          <div>Supportive yogas: {summary.supportive_yogas}</div>

          {summary.note && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#9ca3af" }}>
              {summary.note}
            </div>
          )}

          <div
            style={{
              marginTop: 10,
              fontSize: 12,
              fontStyle: "italic",
              color: "#94a3b8",
              borderTop: "1px solid rgba(148,163,184,0.15)",
              paddingTop: 8,
            }}
          >
            Strength reflects relative prominence in this chart.
            It describes energetic emphasis — not success, failure, or destiny.
          </div>
        </div>
      )}

      {/* INSIGHT FULL DEPTH */}
      {mode === "REAL" &&
        enrichedYogas.map((yoga, idx) => {
          const isOpen = isMultiple ? openIndex === idx : true;

          return (
            <div
              key={idx}
              style={{
                marginBottom: 20,
                padding: 16,
                borderRadius: 12,
                background: "rgba(30,41,59,0.82)",
                border: isOpen
                  ? "1px solid rgba(251,191,36,0.55)"
                  : "1px solid rgba(148,163,184,0.25)",
                cursor: isMultiple ? "pointer" : "default",
              }}
              onClick={() => {
                if (!isMultiple) return;
                setOpenIndex(isOpen ? null : idx);
              }}
            >
              <div style={{ fontWeight: 700 }}>
                {yoga.title}
                {isMultiple && (
                  <span style={{ float: "right", opacity: 0.6 }}>
                    {isOpen ? "▲" : "▼"}
                  </span>
                )}
              </div>

              <p style={{ marginTop: 12, lineHeight: 1.7 }}>
                {yoga.core_meaning.join(" ")}
              </p>

              <div style={{ marginTop: 14 }}>
                <div className="yoga-subsection-title">Activation Pattern</div>
                <BulletList items={yoga.activation_principles} />
              </div>

              {isOpen && (
                <>
                  <div className="yoga-subsection-title">
                    Positive Expression
                  </div>
                  <BulletList items={yoga.positive_expression} />

                  <div className="yoga-subsection-title">
                    Challenging Expression
                  </div>
                  <BulletList items={yoga.challenging_expression} />

                  <div className="yoga-subsection-title">
                    Awareness Guidance
                  </div>
                  <BulletList items={yoga.remedial_guidance} />
                </>
              )}
            </div>
          );
        })}

      {/* GROWTH + PREMIUM_UNLOCK */}
      {mode === "SUMMARY" && <YogaLockBlock />}
    </div>
  );
}

export default YogaAnalysisTab;